import { useEffect, useRef } from "react";
import {
  Grid, IconButton, Typography, TextField, Avatar,
  FormControl, Select, MenuItem, Button, Box, Chip
} from "@mui/material";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parse, isValid } from 'date-fns';
import PropTypes from "prop-types";

import { urlImg } from '../common/helpers';

const ListDetail = ({
  info, isEditing, user, date, cambiarImg, cambiarData,
  setModalPass, setIsEditing, editarEmail, editarDatosPersonales,
  editarTel, confirmarTel, setDate
}) => {
  const latestDateRef = useRef(null);
  // Solo un celular confirmado con un código por SMS vale: el servidor no lo muestra a otros ni lo usa para recuperar la contraseña si no
  const telConfirmado = user.telVerificado === true;

  // Convierte la fecha de la base de datos a un objeto Date al cargar el componente
  useEffect(() => {
    if (info.nacimiento) {
      const parsedDate = parse(info.nacimiento, 'dd-MM-yyyy', new Date());
      if (isValid(parsedDate)) {
        setDate(parsedDate);
      } else {
        console.error('Fecha inválida:', info.nacimiento);
      }
    }
  }, [info.nacimiento]);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    latestDateRef.current = newDate;
  };

  return (
    <Grid mb={2} mt={5} ml={3} size={12} container spacing={3}>
      {/* Top Section: Avatar and Name */}
      <Grid size={12} container sx={{ alignContent: 'center' }} spacing={3}>
        <Grid>
          <label className="avatar-icon" htmlFor="image">
            {
              user.avatar ? (
                <Avatar
                  sx={{ width: 100, height: 100 }}
                  src={`${urlImg}/w_130,h_130,c_fill/v${user.versionAvatar}/users/${user._id}/${user.avatar}`}
                />
              ) : (
                <Avatar sx={{ width: 100, height: 100, fontSize: 40 }}>
                  {user.name ? user.name.charAt(0).toUpperCase() : ''}
                </Avatar>
              )
            }
          </label>
          <input
            accept="image/*"
            id="image"
            type="file"
            style={{ display: "none" }}
            onChange={cambiarImg}
          />
        </Grid>
        <Grid size={true}>
          <Typography color="text.secondary" variant="body2">
            Nombre:
          </Typography>
          {
            isEditing.name ? (
              <Grid container spacing={2} mt={1}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    id="nombre"
                    placeholder="Nombre"
                    name="name"
                    size="small"
                    fullWidth
                    value={info.name}
                    onChange={cambiarData}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    id="apellido"
                    placeholder="Apellido(s)"
                    name="apellido"
                    size="small"
                    fullWidth
                    value={info.apellido}
                    onChange={cambiarData}
                  />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="h4" color="primary" fontWeight="bold">
                {info.name + ' ' + info.apellido}
              </Typography>
            )
          }
        </Grid>
      </Grid>

      {/* Bottom Section: 5 Column Grid */}
      <Grid size={12} container spacing={1}sx={{ justifyContent: 'space-between' }}>

        {/* Fecha de nacimiento */}
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Typography color="text.secondary" variant="body2" mb={1}>
            Fecha de nacimiento
          </Typography>
          {
            isEditing.nacimiento ? (
              // En español y con la semana desde el domingo: el LocalizationProvider de index.js (calendario en tema.js)
              <DatePicker
                value={date}
                onChange={handleDateChange}
                format="dd-MM-yyyy"
              />
            ) : (
              <Typography color="text.primary" variant="body1" fontWeight="500">
                {info.nacimiento || 'No definida'}
              </Typography>
            )
          }
        </Grid>

        {/* Género */}
        <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
          <Typography color="text.secondary" variant="body2" mb={1}>
            Género
          </Typography>
          {
            isEditing.sexo ? (
              <FormControl fullWidth size="small">
                <Select
                  id="sexo"
                  name="sexo"
                  value={info.sexo}
                  onChange={cambiarData}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Selecciona</MenuItem>
                  <MenuItem value="masculino">Masculino</MenuItem>
                  <MenuItem value="femenino">Femenino</MenuItem>
                  <MenuItem value="no quiero decir">No quiero decir</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <Typography color="text.primary" variant="body1" fontWeight="500">
                {info.sexo ? info.sexo.charAt(0).toUpperCase() + info.sexo.slice(1) : 'No definido'}
              </Typography>
            )
          }
        </Grid>

        {/* Correo electrónico */}
        <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
          <Typography color="text.secondary" variant="body2" mb={1}>
            Correo electrónico
          </Typography>
          {
            isEditing.email ? (
              <TextField
                id="email"
                placeholder="Correo electrónico"
                name="email"
                type="email"
                size="small"
                fullWidth
                value={info.email}
                onChange={cambiarData}
                onKeyDown={editarEmail}
                helperText="Presiona Enter y te mandamos un código a ese correo"
              />
            ) : (
              <Grid container alignItems="center">
                <Typography color="text.primary" variant="body1" fontWeight="500" sx={{ wordBreak: 'break-word' }}>
                  {user.email}
                </Typography>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={e => setIsEditing({ ...isEditing, email: true })}
                >
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            )
          }
        </Grid>

        {/* Número celular */}
        <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
          <Typography color="text.secondary" variant="body2" mb={1}>
            Número celular
          </Typography>
          {
            isEditing.tel ? (
              <TextField
                id="tel"
                placeholder="Número celular"
                name="tel"
                type="tel"
                size="small"
                fullWidth
                value={info.tel}
                onChange={cambiarData}
                onKeyDown={editarTel}
                helperText="Escribe 10 dígitos y presiona Enter: te mandamos un código por SMS"
              />
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
                  <Typography color={user.tel ? 'text.primary' : 'text.secondary'} variant="body1" fontWeight="500">
                    {user.tel || 'Sin celular'}
                  </Typography>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={user.tel ? 'Cambiar celular' : 'Agregar celular'}
                    onClick={e => setIsEditing({ ...isEditing, tel: true })}
                  >
                    <DriveFileRenameOutlineIcon fontSize="small" />
                  </IconButton>
                  {user.tel && (
                    <Chip
                      size="small"
                      label={telConfirmado ? 'Confirmado' : 'Sin confirmar'}
                      sx={{ height: 22, fontWeight: 700, color: 'primary.main', bgcolor: telConfirmado ? 'success.claro' : 'warning.main' }}
                    />
                  )}
                </Box>
                {user.tel && !telConfirmado && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, mt: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Solo tú lo ves y todavía no sirve para recuperar tu contraseña.
                    </Typography>
                    <Button variant="contained" color="secondary" size="small" onClick={confirmarTel}>
                      Confirmar por SMS
                    </Button>
                  </Box>
                )}
              </>
            )
          }
        </Grid>

        {/* Contraseña */}
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Typography color="text.secondary" variant="body2" mb={1}>
            Contraseña
          </Typography>
          <Grid container alignItems="center">
            <Typography color="text.primary" variant="body1" fontWeight="500" mr={1}>
              {user.pass === false ? 'Sin contraseña (entras con Google o Facebook)' : '**********'}
            </Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={e => setModalPass(true)}
            >
              <DriveFileRenameOutlineIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      {
        isEditing.name && (
          <Grid size={12} container justifyContent="flex-end" spacing={2} mr={7}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setIsEditing(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={editarDatosPersonales}
            >
              Guardar
            </Button>
          </Grid>
        )
      }
    </Grid>
  );
}

ListDetail.propTypes = {
  info: PropTypes.object.isRequired,
  cambiarData: PropTypes.func.isRequired,
  setModalPass: PropTypes.func.isRequired,
  editarDatosPersonales: PropTypes.func.isRequired,
  editarEmail: PropTypes.func.isRequired,
  editarTel: PropTypes.func.isRequired,
  confirmarTel: PropTypes.func.isRequired,
};

export default ListDetail;
